import React, { useEffect, useRef, useState } from 'react';

const IMAGE_DURATION_MS = 5000;

/**
 * Alternates between a still image and a muted, inline-playing video.
 * Image displays for IMAGE_DURATION_MS, then the video plays in full, then
 * the cycle repeats. Designed for use as a preview thumbnail.
 */
export const MediaRotator = ({ imageSrc, videoSrc, alt = '', className = '', testId }) => {
  const [mode, setMode] = useState('image');
  const videoRef = useRef(null);

  useEffect(() => {
    if (mode !== 'image') return undefined;
    const timer = setTimeout(() => setMode('video'), IMAGE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'video') return;
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play().catch(() => {
      // Autoplay blocked (rare for muted videos). Skip back to image so the
      // user still sees something rather than a frozen frame.
      setMode('image');
    });
  }, [mode]);

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      {mode === 'image' ? (
        <img
          src={imageSrc}
          alt={alt}
          className="w-full h-full object-cover"
          data-testid={testId ? `${testId}-image` : undefined}
        />
      ) : (
        <video
          ref={videoRef}
          src={videoSrc}
          muted
          playsInline
          preload="metadata"
          onEnded={() => setMode('image')}
          className="w-full h-full object-cover"
          data-testid={testId ? `${testId}-video` : undefined}
        />
      )}
    </div>
  );
};

export default MediaRotator;
